import { useState } from "react"
import MobileSheetModal from "./MobileSheetModal"
import { saveCodeToInbox, ApiError } from "../../lib/api"
import { validateEmail } from "../../lib/email"
import { HONEYPOT_FIELD } from "../../lib/botProtection"
import useBotProtection from "../../hooks/useBotProtection"
import Turnstile from "../Turnstile"

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  )
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
      <rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

// The leave-moment prompt: the mobile presentation of EmailCapture.
//
// Not EmailCapture itself. That component is a full panel — four benefit rows,
// a resend affordance, a protection footnote — which is right where it has a
// column to fill and wrong in a sheet the user is trying to get past. This
// reuses the parts that carry behaviour (the same `saveCodeToInbox` endpoint,
// the same `validateEmail`, the same bot-protection field set) and wears the
// sheet's copy and layout instead.
//
// Turnstile renders here for the same reason it does in EmailCapture: the
// server runs the identical bot layer on this endpoint, so omitting the widget
// would send the visitor into a refusal they can't clear.
export default function MobileSaveSheet({ open, token, onClose, onSaved, skipLabel, variant = "leave" }) {
  const [ email, setEmail ] = useState("")
  const [ error, setError ] = useState(null)
  const [ submitting, setSubmitting ] = useState(false)
  const bot = useBotProtection()

  function handleSubmit(e) {
    e.preventDefault()
    const invalid = validateEmail(email)
    if (invalid) {
      // The design asks for no error copy beyond the field state, so an
      // invalid address is a visibly-rejected no-op rather than a message.
      setError(invalid)
      return
    }
    // Before the browser check has a token, the press waits for it (#339).
    bot.submitWhenReady(send)
  }

  async function send() {
    setSubmitting(true)
    setError(null)
    try {
      const res = await saveCodeToInbox(token, email.trim(), bot.fields())
      onSaved(res.email || email.trim())
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : new ApiError("network", err.message)
      // Clear every signal the visitor could be stuck behind (a spent token, an
      // autofilled honeypot, a stale timing stamp) so a retry can pass.
      if (apiErr.kind === "verification_failed") bot.recover()
      setError(apiErr.message)
      setSubmitting(false)
    }
  }

  return (
    <MobileSheetModal open={open} onClose={onClose} label="Save your code">
      <span className="inline-flex h-[42px] w-[42px] items-center justify-center rounded-[13px] bg-voltage text-coal">
        <MailIcon />
      </span>
      <h3 className="mt-1 font-display text-lg font-bold tracking-tight">
        {variant === "intro" ? "Your code is live. Keep it." : "Keep this code editable."}
      </h3>
      {/* The pitch forks on when it appears. "Leave now and this code is just an
          image" is the right warning at the exit and a strange thing to say to
          someone who has been here for two seconds — the intro arm states the
          upside instead of threatening the downside. */}
      <p className="text-sm leading-relaxed text-[color:var(--text-secondary)] text-pretty">
        {variant === "intro"
          ? "Send it to yourself and you can repoint the link later, with no reprint. Skip this and it's just an image."
          : "Leave now and this code is just an image. Save it and you can repoint the link later, with no reprint."}
      </p>

      <form onSubmit={handleSubmit} noValidate className="contents">
        <label htmlFor="mobile-save-email" className="sr-only">Email address</label>
        <input
          id="mobile-save-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@work.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(null) }}
          disabled={bot.held}
          aria-invalid={Boolean(error)}
          className={
            "mt-1 min-h-[50px] w-full rounded px-3.5 text-base " +
            "bg-[color:var(--bg-surface)] text-[color:var(--text-primary)] " +
            "border focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 " +
            (error ? "border-[color:var(--color-fail)]" : "border-[color:var(--border-default)] focus:border-voltage")
          }
        />
        {error && <p className="text-sm text-[color:var(--color-fail)]" aria-live="polite">{error}</p>}

        {/* Same three signals the create path sends. Hidden field first so an
            autofill sweep hits it before the real input. */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
          <label htmlFor="mobile-save-company">Company (leave this blank)</label>
          <input
            id="mobile-save-company"
            name={HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={bot.honeypot}
            onChange={(e) => bot.setHoneypot(e.target.value)}
          />
        </div>
        <Turnstile {...bot.turnstileProps} />

        <div className="mt-1.5 flex flex-col gap-1.5">
          {/* Same rule as the builder: while the browser check has no token,
              a press is held and sent when it lands, never sent empty. */}
          <button
            type="submit"
            disabled={submitting || bot.held}
            className="inline-flex min-h-[50px] w-full items-center justify-center gap-2 rounded-md bg-voltage text-base font-semibold text-coal shadow-voltage transition hover:bg-voltage-dark disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2"
          >
            {submitting ? "Sending…" : bot.turnstilePending || bot.held ? "Checking your browser…" : "Save to my inbox"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] text-sm font-semibold text-[color:var(--text-secondary)] underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 rounded"
          >
            {skipLabel}
          </button>
        </div>
      </form>

      <p className="mt-0.5 flex items-center gap-2 text-xs text-[color:var(--text-secondary)]">
        <LockIcon /> One email. No list, no spam.
      </p>
    </MobileSheetModal>
  )
}
